import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
    departmentsTemplate,
    saveDepartmentTemplate,
    updateDepartmentTemplate,
    deleteDepartmentTemplate,
    reorderDepartmentsTemplate,
} from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Department, DepartmentInput } from 'models/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type DepartmentsContextType = {
    departments: Department[];
    saveDepartment: (variables: { department: DepartmentInput }) => Promise<boolean>;
    updateDepartment: (variables: { department: DepartmentInput }) => Promise<boolean>;
    deleteDepartment: (variables: { departmentId: string }) => Promise<boolean>;
    reorderDepartments: (variables: { departmentIds: string[] }) => Promise<boolean>;
    getDepartmentNameForStep: (stepId: string) => string;
    getDepartmentIdForStep: (stepId: string) => string;
    departmentsLoading: boolean;
    saveDepartmentLoading: boolean;
    updateDepartmentLoading: boolean;
    deleteDepartmentLoading: boolean;
};

const DepartmentsContext = createContext<DepartmentsContextType | undefined>(undefined);

export function DepartmentsProvider({ children }: { children: ReactNode }) {
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();

    const {
        data: departmentsData,
        loading: departmentsLoading,
        refetch: refetchDepartments,
    } = useQuery(departmentsTemplate, {
        fetchPolicy: 'no-cache',
        // DD-3589: the backend refuses departments to external users below the elevated tier.
        skip: !permissions.canReadDepartments(currentUser.role),
    });

    const [saveDepartmentMutation, { loading: saveDepartmentLoading }] = useMutation(saveDepartmentTemplate);
    const [updateDepartmentMutation, { loading: updateDepartmentLoading }] = useMutation(updateDepartmentTemplate);
    const [deleteDepartmentMutation, { loading: deleteDepartmentLoading }] = useMutation(deleteDepartmentTemplate);
    const [reorderDepartmentsMutation] = useMutation(reorderDepartmentsTemplate);

    const saveDepartment = async (variables: { department: DepartmentInput }) => {
        try {
            const result = await saveDepartmentMutation({ variables });
            await refetchDepartments();
            return result.data.saveDepartment;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const updateDepartment = async (variables: { department: DepartmentInput }) => {
        try {
            const result = await updateDepartmentMutation({ variables });
            await refetchDepartments();
            return result.data.updateDepartment;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const deleteDepartment = async (variables: { departmentId: string }) => {
        try {
            const result = await deleteDepartmentMutation({ variables });
            await refetchDepartments();
            return result.data.deleteDepartment;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const reorderDepartments = async (variables: { departmentIds: string[] }) => {
        try {
            const result = await reorderDepartmentsMutation({ variables });
            await refetchDepartments();
            return result.data.reorderDepartments;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const getDepartmentNameForStep = useCallback(
        (stepId: string): string => {
            const departments = departmentsData?.departments ?? [];
            const dept = departments.find((d: Department) => d.workflowSteps.some((s) => s._id === stepId));
            return dept ? dept.name : '';
        },
        [departmentsData],
    );

    const getDepartmentIdForStep = useCallback(
        (stepId: string): string => {
            const departments = departmentsData?.departments ?? [];
            const dept = departments.find((d: Department) => d.workflowSteps.some((s) => s._id === stepId));
            return dept?._id || '';
        },
        [departmentsData],
    );

    const value = useMemo(
        () => ({
            departments: departmentsData?.departments ?? [],
            saveDepartment,
            updateDepartment,
            deleteDepartment,
            reorderDepartments,
            getDepartmentNameForStep,
            getDepartmentIdForStep,
            departmentsLoading,
            saveDepartmentLoading,
            updateDepartmentLoading,
            deleteDepartmentLoading,
        }),
        [
            departmentsData,
            departmentsLoading,
            saveDepartmentLoading,
            updateDepartmentLoading,
            deleteDepartmentLoading,
            getDepartmentNameForStep,
        ],
    );

    return <DepartmentsContext.Provider value={value}>{children}</DepartmentsContext.Provider>;
}

export function useDepartments() {
    const context = useContext(DepartmentsContext);
    if (!context) {
        throw new Error('useDepartments must be used within a DepartmentsProvider');
    }
    return context;
}
